<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

import { Nullable } from '@nova/core';

import NextStep from '@/components/NextStep.vue';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import useNotify from '@/composables/useNotify';
import { useDialogTrigger } from '@/composables/useEventBasedDialog';
import checkoutService from '@/services/checkoutService';

const checkoutFlow = useCheckoutFlow();
const { warehouseData } = useWarehouseInfo();

const { notify } = useNotify();

const { triggerDialog: triggerCompleteCheckoutDialog } = useDialogTrigger(
  'open-complete-checkout-dialog'
);

const isDocumentLoading = ref(false);
const bolDocumentString = ref<Nullable<string>>(null);

const isFormValid = ref(false);
const driverName = ref('');
const eSignatureAgreement = ref(false);

const rules = {
  driverName: (value: string) => {
    if (!value) return 'Driver name is required';
    return true;
  },
  eSignatureAgreement: (value: boolean) => {
    if (!value) return 'You must agree to the e-signature terms';
    return true;
  }
};

const fetchBolDocument = async () => {
  isDocumentLoading.value = true;
  try {
    const bolData = await checkoutService.getBol(
      warehouseData.value.id,
      checkoutFlow.appointmentId.value
    );
    const bolPdf = await checkoutService.previewBolPdf(
      warehouseData.value.id,
      bolData.originalDoc.id
    );
    const blob = new Blob([bolPdf], { type: 'application/pdf' });
    bolDocumentString.value = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=0';
  } catch (error) {
    notify('Error fetching BOL document, please try again later', 'error');
    console.error('Error fetching BOL document:', error);
  } finally {
    isDocumentLoading.value = false;
  }
};

onMounted(async () => {
  await fetchBolDocument();
});

watch(isFormValid, async newVal => {
  checkoutFlow.setStepValid(CheckoutFlowStep.ViewBolPage, newVal);
});

const handleSubmit = async () => {
  checkoutFlow.setIsStepLoading(true);
  try {
    await checkoutService.signBol(warehouseData.value.id, {
      appointmentId: checkoutFlow.appointmentId.value,
      driverFullName: driverName.value
    });

    triggerCompleteCheckoutDialog();
  } catch (error) {
    let errorMessage = 'An error occurred when signing the e-BOL, please try again';

    if (typeof error?.response?.data?.message === 'string') {
      errorMessage = error.response.data.message;
    }

    notify(errorMessage, 'error');

    console.error('Error submitting e-signature:', error);
  } finally {
    checkoutFlow.setIsStepLoading(false);
  }
};
</script>

<template>
  <div class="d-flex flex-column gap-6 px-4 fill-height">
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      style="align-self: flex-start"
      @click="checkoutFlow.setPreviousStep(CheckoutFlowStep.ValidationPage)">
      Back
    </v-btn>

    <h3 class="heading-md-bold">Sign E-BOL</h3>

    <div style="width: 100%">
      <div
        v-if="isDocumentLoading"
        class="d-flex flex-column align-center justify-center gap-4 py-16 my-auto">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="text-body-1 text-grey">Loading BOL document, please wait...</p>
      </div>
      <iframe
        v-else
        :src="bolDocumentString"
        style="height: 400px; width: 100%; border: none"
        aria-label="BOL document"></iframe>
    </div>

    <v-form ref="form" @submit.prevent="handleSubmit" v-model="isFormValid" fast-fail>
      <div class="checkout-form-body d-flex flex-column gap-3">
        <div class="d-flex flex-column gap-2 form-field">
          <v-label class="typography-body-sm-bold required-field">Driver's Name</v-label>
          <v-text-field
            v-model="driverName"
            variant="outlined"
            density="default"
            placeholder="E.g. John Levy"
            :rules="[rules.driverName]"
            validate-on="input"></v-text-field>
        </div>

        <div class="d-flex flex-column form-field">
          <v-checkbox
            v-model="eSignatureAgreement"
            :rules="[rules.eSignatureAgreement]"
            density="compact"
            label="I agree this e-signature is legally binding and applies to this BOL"
            class="typography-body-sm"></v-checkbox>
        </div>
      </div>
    </v-form>
  </div>

  <next-step
    :current-step="checkoutFlow.step"
    :is-step-valid="checkoutFlow.isStepValid(checkoutFlow.step.value)"
    :on-step-complete="handleSubmit" />
</template>

<style scoped lang="scss">
.checkout-form-body .form-field {
  .v-input {
    margin-top: 0;
  }

  .v-label {
    position: relative;
    color: $color-text-secondary;
    align-self: flex-start;
    opacity: 1;
  }
}
</style>
